/**
 * Character materials — 100% procedural, zero external assets.
 *
 * Every warrior in the game shares ONE material per faction. That material is
 * driven by a hand-packed texture atlas painted with canvas 2D at boot:
 *
 *   albedo   (per faction, sRGB)   base colour
 *   normal   (shared)              derived by Sobel from a painted height field
 *   orm      (shared)              R = ao (unused), G = roughness, B = metalness
 *
 * Because the albedo carries the faction colours, the GEOMETRY is faction
 * independent: the crowd renderer can instance one buffer for both armies and
 * simply swap the material. Per-unit variation (sash hue, cloth value, skin
 * tone) rides on the vertex-colour attribute, which multiplies the albedo.
 *
 * ATLAS LAYOUT (1024 x 1024, y measured from the top)
 *   +----------------------+----------------------+
 *   |  face      512x512   |  cape      512x512   |   0..512
 *   +------+------+--------+--------+-------------+
 *   |lamel |cloth |steel   |gold    |             |   512..768  (256 each)
 *   +------+------+--------+---+----+
 *   |skin  |leath |silk    |hr |wd  |                768..1024  (256 / 128)
 *   +------+------+--------+---+----+
 *                          |ir |neu |
 *
 * The lamellar / cloth / steel tiles are painted with their pattern already at
 * final density, so mapping a cuirass 1:1 onto a 256px tile still yields ~60
 * individual plates. Nothing relies on tiled UV repeats.
 */
import * as THREE from 'three';
import { RNG } from '../core/rng.js';

const AW = 1024, AH = 1024;

/** Tile rectangles in canvas pixels. */
export const TILES = {
  face: { x: 0, y: 0, w: 512, h: 512 },
  cape: { x: 512, y: 0, w: 512, h: 512 },
  lamellar: { x: 0, y: 512, w: 256, h: 256 },
  cloth: { x: 256, y: 512, w: 256, h: 256 },
  steel: { x: 512, y: 512, w: 256, h: 256 },
  gold: { x: 768, y: 512, w: 256, h: 256 },
  skin: { x: 0, y: 768, w: 256, h: 256 },
  leather: { x: 256, y: 768, w: 256, h: 256 },
  silk: { x: 512, y: 768, w: 256, h: 256 },
  hair: { x: 768, y: 768, w: 128, h: 128 },
  wood: { x: 896, y: 768, w: 128, h: 128 },
  iron: { x: 768, y: 896, w: 128, h: 128 },
  neutral: { x: 896, y: 896, w: 128, h: 128 },
};

/** Tile rect expressed in UV space (for geom.tileUV). */
export const UVRECT = {};
for (const k in TILES) {
  const t = TILES[k];
  UVRECT[k] = { u: t.x / AW, v: t.y / AH, w: t.w / AW, h: t.h / AH };
}

/* ------------------------------------------------------------------ palettes */

export const FACTIONS = {
  /**
   * The player's army. Vermilion lacquer, steel, Majapahit gold.
   *
   * The chromatic roles moved from indigo to red for merah-putih; every value
   * (luminance) was matched to what it replaced rather than picked freshly —
   * old cloth #2b3f76 sat at 0.25 relative luminance and the new one sits at
   * 0.25 too. That is deliberate: this palette is lit by an authored low sun
   * and the ramps below were tuned against those values, so a repaint that
   * keeps hue free and value fixed is the one least likely to blow out.
   */
  sui: {
    name: 'Wilwatikta', team: 0,
    cloth: '#8f2d24', clothLo: '#4d1410', clothHi: '#c8493a',
    lam: '#a8362b', lamLo: '#59180f', lamHi: '#dd5f47',
    trim: '#e8bb4c', trimLo: '#8a6420', trimHi: '#ffe9a8',
    silk: '#e0ae3c', silkLo: '#8c621a', silkHi: '#ffe6a0',
    // Warm metal, and this is the third surface merah putih had to reach.
    // The repaint moved cloth, lam, cape and accent and left the metal alone,
    // so Wilwatikta kept the bluest steel and iron of the three factions --
    // #9fabbd and #5c636d, cooler than the neutrals and far cooler than Segara
    // Kidul's warm #6f6a67 -- from back when this side WAS the blue one. On red
    // lamellar that reads as your own men wearing the enemy's colour on their
    // heads (shots/metal_before/frontendBattle/002_03-fighting.png). Same rule
    // as the cloth repaint: hue rotated warm, relative luminance held to within
    // 0.35 of what it replaced, so nothing re-exposes. These tiles are also the
    // blades, greaves, bracers and boot plates, which is the point -- the whole
    // of Wilwatikta's metalwork goes bronze rather than just the helmets.
    steel: '#b6a991', steelLo: '#6c6253', steelHi: '#f6ecdc',
    leather: '#4b3625', leatherLo: '#241a10', leatherHi: '#7c5c3c',
    iron: '#6b6154', ironLo: '#363028', ironHi: '#b7ab98',
    cape: '#a3302a', capeLo: '#54120f', capeHi: '#d9564a',
    wood: '#6a4a2c', woodLo: '#3a2716', woodHi: '#a2794c',
    hair: '#1a1512', hairHi: '#4a3a2c',
    // Exposure-safe skin ramp. Hue ratios stay warm/unchanged; only value is
    // compressed (base -18%, shadow -14%, highlight -24%) so the sun cannot
    // merge the face, hands and silk headband into one pale band.
    skin: '#a57e5d', skinLo: '#795438', skinHi: '#b29677',
    accent: '#ff9a7c',
  },
  /**
   * The invader off the Southern Sea. Deep indigo lacquer, pewter, teal silk.
   * Same rule as above: hue moved, luminance held. Old cloth #8a2126 was 0.22
   * and the new one is 0.22; the bronze trim went pewter at the same value so
   * the gold-versus-metal read between the two sides survives the swap.
   */
  kai: {
    name: 'Segara Kidul', team: 1,
    cloth: '#1f3b57', clothLo: '#101f30', clothHi: '#3a6a94',
    lam: '#24506b', lamLo: '#122a3a', lamHi: '#4a89ad',
    trim: '#8fa6b4', trimLo: '#4e5f6a', trimHi: '#d2e2ec',
    silk: '#6b918c', silkLo: '#3a5350', silkHi: '#a8ccc6',
    steel: '#6f6a67', steelLo: '#3a3735', steelHi: '#b6b0aa',
    leather: '#3a2b25', leatherLo: '#1d1512', leatherHi: '#68524a',
    iron: '#403c3d', ironLo: '#201d1e', ironHi: '#837c7f',
    cape: '#1c4a5e', capeLo: '#0e2530', capeHi: '#3f8298',
    wood: '#4e3520', woodLo: '#281a0f', woodHi: '#7d5b39',
    hair: '#15100e', hairHi: '#3c2f24',
    skin: '#9d7151', skinLo: '#714c30', skinHi: '#ad8b6c',
    accent: '#7fe0d8',
  },
  /** Neutral / peasant levies and props. */
  wei: {
    name: 'Neutral', team: 2,
    cloth: '#4a4638', clothLo: '#24221b', clothHi: '#6e6a56',
    lam: '#4f4a3c', lamLo: '#282419', lamHi: '#7a7360',
    trim: '#a89257', trimLo: '#5d5029', trimHi: '#ddc98d',
    silk: '#b8a55f', silkLo: '#645a2c', silkHi: '#e6d79d',
    steel: '#8b8b8b', steelLo: '#4e4e4e', steelHi: '#d2d2d2',
    leather: '#3f3226', leatherLo: '#1e1710', leatherHi: '#6d5942',
    iron: '#3f4042', ironLo: '#1d1e20', ironHi: '#797b7e',
    cape: '#4c4739', capeLo: '#232016', capeHi: '#77705a',
    wood: '#5d4228', woodLo: '#31220f', woodHi: '#8f6a45',
    hair: '#1a1512', hairHi: '#463629',
    skin: '#a17959', skinLo: '#775237', skinHi: '#b09476',
    accent: '#cfd7c0',
  },
};

/* ------------------------------------------------------------------ painter */

function makeCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

function hexToRgb(h) {
  if (h[0] !== '#') return [255, 0, 255];
  const v = parseInt(h.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}
function rgbStr(r, g, b, a) {
  return a === undefined ? `rgb(${r | 0},${g | 0},${b | 0})` : `rgba(${r | 0},${g | 0},${b | 0},${a})`;
}
function mulHex(h, k) {
  const [r, g, b] = hexToRgb(h);
  return rgbStr(Math.min(255, r * k), Math.min(255, g * k), Math.min(255, b * k));
}

/**
 * Replays the same 2D drawing commands onto every atlas layer at once, each
 * layer resolving the "material spec" differently. Writing a tile therefore
 * automatically produces albedo (per faction), height and roughness/metalness.
 *
 * spec = { c:'paletteKey'|'#hex', k:brightnessMul, h:height01, r:rough01,
 *          m:metal01, a:alpha }
 */
class Painter {
  /** @param {Array} targets  pre-built layer descriptors (see makeLayers). */
  constructor(targets) { this.targets = targets; }
  style(t, s) {
    if (t.kind === 'a') {
      let col = s.c || 'neutral';
      if (col[0] !== '#') col = t.pal[col] || '#ff00ff';
      return s.k !== undefined ? mulHex(col, s.k) : col;
    }
    if (t.kind === 'h') { const v = Math.round(255 * (s.h ?? 0.5)); return rgbStr(v, v, v); }
    const ao = Math.round(255 * (s.ao ?? 1));
    return rgbStr(ao, Math.round(255 * (s.r ?? 0.6)), Math.round(255 * (s.m ?? 0)));
  }
  /** fn(ctx) issues path commands; the painter fills them on every layer. */
  fill(s, fn) {
    for (const t of this.targets) {
      const c = t.ctx; c.save();
      c.globalAlpha = s.a ?? 1;
      c.fillStyle = this.style(t, s);
      c.beginPath(); fn(c, t); c.fill();
      c.restore();
    }
  }
  stroke(s, lw, fn) {
    for (const t of this.targets) {
      const c = t.ctx; c.save();
      c.globalAlpha = s.a ?? 1;
      c.strokeStyle = this.style(t, s);
      c.lineWidth = lw; c.lineCap = s.cap || 'butt'; c.lineJoin = 'round';
      c.beginPath(); fn(c, t); c.stroke();
      c.restore();
    }
  }
  rect(x, y, w, h, s) { this.fill(s, c => c.rect(x, y, w, h)); }
  /** Linear gradient fill. stops = [[t, spec], ...] */
  lgrad(x0, y0, x1, y1, stops, fn, a = 1) {
    for (const t of this.targets) {
      const c = t.ctx; c.save(); c.globalAlpha = a;
      const g = c.createLinearGradient(x0, y0, x1, y1);
      for (const [p, s] of stops) g.addColorStop(p, this.style(t, s));
      c.fillStyle = g; c.beginPath(); fn(c, t); c.fill(); c.restore();
    }
  }
  rgrad(cx, cy, r0, r1, stops, fn, a = 1) {
    for (const t of this.targets) {
      const c = t.ctx; c.save(); c.globalAlpha = a;
      const g = c.createRadialGradient(cx, cy, r0, cx, cy, r1);
      for (const [p, s] of stops) g.addColorStop(p, this.style(t, s));
      c.fillStyle = g; c.beginPath(); fn(c, t); c.fill(); c.restore();
    }
  }
  /** Clip subsequent draws to a rect (used to keep tiles from bleeding). */
  clip(x, y, w, h, fn) {
    for (const t of this.targets) { t.ctx.save(); t.ctx.beginPath(); t.ctx.rect(x, y, w, h); t.ctx.clip(); }
    fn();
    for (const t of this.targets) t.ctx.restore();
  }
}

/* ------------------------------------------------------------------ tiles */

function tileLamellar(P, T, rng) {
  const { x, y, w, h } = T;
  P.lgrad(x, y, x, y + h, [
    [0, { c: 'lamHi', k: 0.84, h: 0.58, r: 0.36, m: 0.10 }],
    [0.45, { c: 'lam', k: 0.94, h: 0.52, r: 0.40, m: 0.09 }],
    [1, { c: 'lamLo', k: 1.08, h: 0.34, r: 0.50, m: 0.07 }],
  ], ctx => ctx.rect(x, y, w, h));
  // Medium, low-contrast plates survive mipmapping as broad armour bands. The
  // previous 14x16 field plus a highlight/rivet on every cell became reptile
  // glitter once a shoulder occupied only tens of screen pixels.
  const rows = 10, cols = 12;
  const pw = w / cols, ph = h / rows;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const off = (r % 2) * pw * 0.5;
      const px = x + c * pw + off - pw * 0.5, py = y + r * ph;
      const jit = 0.95 + rng.next() * 0.09;
      const iw = pw * 0.95, ih = ph * 1.08;
      // Lacquer is a dielectric: broad value rolloff, restrained metallic fleck.
      P.lgrad(px, py, px, py + ih, [
        [0.00, { c: 'lamHi', k: jit * 0.88, h: 0.64, r: 0.32, m: 0.12 }],
        [0.24, { c: 'lam', k: jit, h: 0.66, r: 0.36, m: 0.10 }],
        [0.82, { c: 'lam', k: jit * 0.88, h: 0.50, r: 0.43, m: 0.08 }],
        [1.00, { c: 'lamLo', k: jit * 0.92, h: 0.34, r: 0.52, m: 0.06 }],
      ], ctx => rr(ctx, px, py, iw, ih, pw * 0.30));
      // A soft lip and lacing explain construction without creating one bright
      // pixel per scale.
      P.fill({ c: 'lamHi', k: 0.94, h: 0.70, r: 0.30, m: 0.12, a: 0.12 },
        ctx => rr(ctx, px + iw * 0.12, py + ih * 0.05, iw * 0.76, ih * 0.09, ih * 0.04));
      for (const lx of [px + iw * 0.30, px + iw * 0.70]) {
        P.fill({ c: 'trimLo', k: 0.82, h: 0.54, r: 0.68, m: 0.02, a: 0.34 },
          ctx => rr(ctx, lx - pw * 0.030, py + ih * 0.18, pw * 0.060, ih * 0.52, pw * 0.03));
      }
      if ((r + c) % 3 === 0) P.fill({ c: 'trim', k: 0.74, h: 0.68, r: 0.34, m: 0.82, a: 0.42 },
        ctx => ctx.arc(px + iw * 0.5, py + ih * 0.21, pw * 0.035, 0, 7));
    }
  }
  // Low-frequency grime groups plates into one cuirass at distance.
  for (let i = 0; i < 24; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 14 + rng.next() * 34;
    P.rgrad(gx, gy, 0, gr, [[0, { c: 'lamLo', k: 0.7, h: 0.5, r: 0.55, m: 0.05 }], [1, { c: 'lamLo', k: 0.7, h: 0.5, r: 0.55, m: 0.05, a: 0 }]],
      ctx => ctx.arc(gx, gy, gr, 0, 7), 0.10);
  }
}

function tileCloth(P, T, rng) {
  const { x, y, w, h } = T;
  P.lgrad(x, y, x + w, y, [
    [0, { c: 'clothLo', k: 0.92, h: 0.43, r: 0.94, m: 0 }],
    [0.30, { c: 'cloth', h: 0.50, r: 0.92, m: 0 }],
    [0.62, { c: 'clothHi', k: 0.82, h: 0.56, r: 0.88, m: 0 }],
    [1, { c: 'clothLo', k: 0.94, h: 0.44, r: 0.94, m: 0 }],
  ], ctx => ctx.rect(x, y, w, h));
  // Broad dye/fold variation, not texel-scale weave shimmer.
  for (let i = 0; i < 20; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 24 + rng.next() * 54;
    const dark = rng.next() < 0.5;
    P.rgrad(gx, gy, 0, gr, [[0, { c: dark ? 'clothLo' : 'clothHi', h: 0.5, r: 0.9 }], [1, { c: dark ? 'clothLo' : 'clothHi', h: 0.5, r: 0.9, a: 0 }]],
      ctx => ctx.arc(gx, gy, gr, 0, 7), 0.12);
  }
  for (let i = 0; i < 8; i++) {
    const fx = x + (i + 0.5) * w / 8;
    P.stroke({ c: 'clothLo', h: 0.43, r: 0.95, a: 0.10 }, w / 24,
      ctx => { ctx.moveTo(fx, y); ctx.bezierCurveTo(fx - 8, y + h * 0.35, fx + 10, y + h * 0.72, fx - 4, y + h); });
    P.stroke({ c: 'clothHi', h: 0.56, r: 0.88, a: 0.07 }, w / 42,
      ctx => { ctx.moveTo(fx + w / 30, y); ctx.bezierCurveTo(fx + 2, y + h * 0.4, fx + 14, y + h * 0.7, fx, y + h); });
  }
  for (let j = 0; j < h; j += 12) P.stroke({ c: 'clothLo', h: 0.48, r: 0.94, a: 0.06 }, 0.8,
    ctx => { ctx.moveTo(x, y + j); ctx.lineTo(x + w, y + j); });
}

function tileSteel(P, T, rng) {
  const { x, y, w, h } = T;
  P.lgrad(x, y, x, y + h, [
    [0, { c: 'steelHi', h: 0.55, r: 0.20, m: 0.95 }],
    [0.45, { c: 'steel', h: 0.5, r: 0.26, m: 0.96 }],
    [1, { c: 'steelLo', h: 0.45, r: 0.34, m: 0.94 }],
  ], ctx => ctx.rect(x, y, w, h));
  // Broad directional brushing; hundreds of sub-pixel streaks read as noise.
  for (let i = 0; i < 72; i++) {
    const gy = y + rng.next() * h;
    const lw = 0.9 + rng.next() * 2.4;
    const light = rng.next() < 0.5;
    const x0 = x + rng.next() * w * 0.4, x1 = x0 + w * (0.3 + rng.next() * 0.7);
    P.stroke({ c: light ? 'steelHi' : 'steelLo', h: 0.5 + (light ? 0.03 : -0.03), r: light ? 0.22 : 0.38, m: 0.94, a: 0.08 + rng.next() * 0.10 },
      lw, ctx => { ctx.moveTo(x0, gy); ctx.lineTo(Math.min(x + w, x1), gy); });
  }
  // deep scratches + a couple of dents
  for (let i = 0; i < 8; i++) {
    const gy = y + rng.next() * h, gx = x + rng.next() * w * 0.6;
    P.stroke({ c: 'steelHi', h: 0.58, r: 0.12, m: 0.98, a: 0.5 }, 1.0,
      ctx => { ctx.moveTo(gx, gy); ctx.lineTo(gx + 20 + rng.next() * 70, gy + (rng.next() - 0.5) * 6); });
  }
  for (let i = 0; i < 6; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 6 + rng.next() * 16;
    P.rgrad(gx, gy, 0, gr, [[0, { c: 'steelLo', h: 0.38, r: 0.42, m: 0.9 }], [1, { c: 'steelLo', h: 0.5, r: 0.28, m: 0.95, a: 0 }]],
      ctx => ctx.arc(gx, gy, gr, 0, 7), 0.4);
  }
}

function tileGold(P, T, rng) {
  const { x, y, w, h } = T;
  P.lgrad(x, y, x + w * 0.4, y + h, [
    [0, { c: 'trim', k: 1.06, h: 0.55, r: 0.30, m: 0.92 }],
    [0.4, { c: 'trim', k: 0.92, h: 0.5, r: 0.34, m: 0.92 }],
    [1, { c: 'trimLo', k: 1.05, h: 0.45, r: 0.40, m: 0.90 }],
  ], ctx => ctx.rect(x, y, w, h));
  // Large engraved scrolls remain trim at distance instead of gold glitter.
  const step = w / 4;
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      const cx = x + c * step + step / 2, cy = y + r * step + step / 2;
      P.stroke({ c: 'trimLo', k: 0.7, h: 0.34, r: 0.42, m: 0.92, a: 0.62 }, step * 0.07, ctx => {
        ctx.arc(cx, cy, step * 0.28, 0.6, 5.1);
      });
      P.stroke({ c: 'trimHi', k: 0.90, h: 0.62, r: 0.24, m: 0.94, a: 0.48 }, step * 0.035, ctx => {
        ctx.arc(cx, cy - step * 0.03, step * 0.30, 0.7, 4.9);
      });
    }
  }
  for (let i = 0; i < 16; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 8 + rng.next() * 20;
    P.rgrad(gx, gy, 0, gr, [[0, { c: 'trim', k: 1.05, h: 0.56, r: 0.26, m: 0.92 }], [1, { c: 'trim', h: 0.5, r: 0.30, m: 0.92, a: 0 }]],
      ctx => ctx.arc(gx, gy, gr, 0, 7), 0.12);
  }
}

function tileLeather(P, T, rng) {
  const { x, y, w, h } = T;
  P.rect(x, y, w, h, { c: 'leather', h: 0.5, r: 0.74, m: 0.02 });
  for (let i = 0; i < 70; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 8 + rng.next() * 34;
    P.rgrad(gx, gy, 0, gr, [[0, { c: rng.next() < 0.5 ? 'leatherLo' : 'leatherHi', h: 0.5, r: 0.74 }], [1, { c: 'leatherLo', h: 0.5, r: 0.74, a: 0 }]],
      ctx => ctx.arc(gx, gy, gr, 0, 7), 0.22);
  }
  // grain speckle
  for (let i = 0; i < 1400; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 0.5 + rng.next() * 1.5;
    P.fill({ c: rng.next() < 0.6 ? 'leatherLo' : 'leatherHi', h: rng.next() < 0.5 ? 0.42 : 0.58, r: 0.78, a: 0.35 },
      ctx => ctx.arc(gx, gy, gr, 0, 7));
  }
  // stitching along the edges
  for (const [sx, sy, dx, dy] of [[10, 10, 1, 0], [10, h - 10, 1, 0], [10, 10, 0, 1], [w - 10, 10, 0, 1]]) {
    for (let i = 0; i < (dx ? w - 20 : h - 20); i += 11) {
      const px = x + sx + dx * i, py = y + sy + dy * i;
      P.stroke({ c: 'trimLo', k: 1.3, h: 0.66, r: 0.6, m: 0.05, a: 0.8 }, 2.2,
        ctx => { ctx.moveTo(px, py); ctx.lineTo(px + dx * 6, py + dy * 6); });
    }
  }
}

function tileSilk(P, T, rng) {
  const { x, y, w, h } = T;
  P.lgrad(x, y, x + w, y + h, [
    [0, { c: 'silkHi', h: 0.55, r: 0.34, m: 0.06 }],
    [0.35, { c: 'silk', h: 0.5, r: 0.42, m: 0.05 }],
    [0.7, { c: 'silkHi', h: 0.54, r: 0.32, m: 0.06 }],
    [1, { c: 'silkLo', h: 0.46, r: 0.48, m: 0.04 }],
  ], ctx => ctx.rect(x, y, w, h));
  for (let i = -h; i < w; i += 18) {
    P.stroke({ c: 'silkHi', h: 0.56, r: 0.34, a: 0.11 }, 4.0, ctx => { ctx.moveTo(x + i, y); ctx.lineTo(x + i + h, y + h); });
    P.stroke({ c: 'silkLo', h: 0.44, r: 0.52, a: 0.09 }, 2.5, ctx => { ctx.moveTo(x + i + 8, y); ctx.lineTo(x + i + 8 + h, y + h); });
  }
  for (let j = 0; j < h; j += 8) {
    P.stroke({ c: 'silkLo', h: 0.48, r: 0.48, a: 0.07 }, 1, ctx => { ctx.moveTo(x, y + j); ctx.lineTo(x + w, y + j); });
  }
}

function tileSkin(P, T, rng) {
  const { x, y, w, h } = T;
  P.rect(x, y, w, h, { c: 'skin', h: 0.5, r: 0.78, m: 0 });
  for (let i = 0; i < 24; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 10 + rng.next() * 44;
    P.rgrad(gx, gy, 0, gr, [[0, { c: rng.next() < 0.5 ? 'skinLo' : 'skinHi', h: 0.5, r: 0.74 }], [1, { c: 'skinLo', h: 0.5, r: 0.78, a: 0 }]],
      ctx => ctx.arc(gx, gy, gr, 0, 7), 0.10);
  }
  for (let i = 0; i < 180; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h;
    P.fill({ c: 'skinLo', h: 0.47, r: 0.80, a: 0.05 }, ctx => ctx.arc(gx, gy, 0.8 + rng.next() * 1.3, 0, 7));
  }
}

function tileHair(P, T, rng) {
  const { x, y, w, h } = T;
  P.rect(x, y, w, h, { c: 'hair', h: 0.5, r: 0.52, m: 0.02 });
  for (let i = 0; i < 260; i++) {
    const gx = x + rng.next() * w;
    const hi = rng.next() < 0.35;
    P.stroke({ c: hi ? 'hairHi' : 'hair', k: hi ? 1.0 : 0.6, h: hi ? 0.68 : 0.36, r: 0.46, a: 0.3 + rng.next() * 0.35 },
      0.7 + rng.next() * 1.6, ctx => { ctx.moveTo(gx, y); ctx.bezierCurveTo(gx + 6, y + h * 0.4, gx - 6, y + h * 0.7, gx + (rng.next() - 0.5) * 12, y + h); });
  }
}

function tileWood(P, T, rng) {
  const { x, y, w, h } = T;
  P.rect(x, y, w, h, { c: 'wood', h: 0.5, r: 0.76, m: 0 });
  for (let i = 0; i < 90; i++) {
    const gx = x + rng.next() * w;
    P.stroke({ c: rng.next() < 0.5 ? 'woodLo' : 'woodHi', h: rng.next() < 0.5 ? 0.4 : 0.6, r: 0.78, a: 0.28 },
      0.8 + rng.next() * 2.4, ctx => { ctx.moveTo(gx, y); ctx.lineTo(gx + (rng.next() - 0.5) * 8, y + h); });
  }
}

function tileIron(P, T, rng) {
  const { x, y, w, h } = T;
  P.rect(x, y, w, h, { c: 'iron', h: 0.5, r: 0.44, m: 0.88 });
  for (let i = 0; i < 90; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 4 + rng.next() * 13;
    P.rgrad(gx, gy, 0, gr, [[0, { c: rng.next() < 0.5 ? 'ironLo' : 'ironHi', h: rng.next() < 0.5 ? 0.36 : 0.66, r: 0.4, m: 0.9 }],
    [1, { c: 'iron', h: 0.5, r: 0.44, m: 0.88, a: 0 }]], ctx => ctx.arc(gx, gy, gr, 0, 7), 0.5);
  }
}

function tileNeutral(P, T) {
  P.rect(T.x, T.y, T.w, T.h, { c: '#b4b4b4', h: 0.5, r: 0.62, m: 0.0 });
}

/* ---- the cape: faction field, border band, and a bold heraldic sigil ---- */
function tileCape(P, T, rng, factionIdx) {
  const { x, y, w, h } = T;
  P.lgrad(x, y, x, y + h, [
    [0, { c: 'capeHi', h: 0.5, r: 0.82 }],
    [0.35, { c: 'cape', h: 0.5, r: 0.86 }],
    [1, { c: 'capeLo', h: 0.5, r: 0.9 }],
  ], ctx => ctx.rect(x, y, w, h));
  for (let i = 0; i < 40; i++) {
    const gx = x + rng.next() * w, gy = y + rng.next() * h, gr = 24 + rng.next() * 90;
    P.rgrad(gx, gy, 0, gr, [[0, { c: rng.next() < 0.5 ? 'capeLo' : 'capeHi', h: 0.5, r: 0.86 }], [1, { c: 'capeLo', h: 0.5, r: 0.86, a: 0 }]],
      ctx => ctx.arc(gx, gy, gr, 0, 7), 0.2);
  }
  // Coarse weave survives the mip chain as cloth instead of screen-door noise.
  for (let j = 0; j < h; j += 12) P.stroke({ c: 'capeLo', h: 0.46, r: 0.90, a: 0.09 }, 1.4, ctx => { ctx.moveTo(x, y + j); ctx.lineTo(x + w, y + j); });
  for (let i = 0; i < w; i += 12) P.stroke({ c: 'capeHi', h: 0.54, r: 0.86, a: 0.06 }, 1.4, ctx => { ctx.moveTo(x + i, y); ctx.lineTo(x + i, y + h); });

  // border band along all four edges
  const bw = w * 0.050;
  for (const [bx, by, bwid, bhei] of [[x, y, w, bw], [x, y + h - bw, w, bw], [x, y, bw, h], [x + w - bw, y, bw, h]]) {
    P.rect(bx, by, bwid, bhei, { c: 'trim', h: 0.62, r: 0.26, m: 0.9 });
  }
  P.stroke({ c: 'trimHi', h: 0.72, r: 0.16, m: 1 }, 3, ctx => ctx.rect(x + bw, y + bw, w - bw * 2, h - bw * 2));
  P.stroke({ c: 'trimLo', h: 0.4, r: 0.4, m: 0.9 }, 2, ctx => ctx.rect(x + bw * 0.35, y + bw * 0.35, w - bw * 0.7, h - bw * 0.7));

  // heraldic sigil
  const cx = x + w * 0.5, cy = y + h * 0.42, R = w * 0.24;
  P.fill({ c: 'capeLo', k: 0.7, h: 0.44, r: 0.9, a: 0.8 }, ctx => ctx.arc(cx, cy, R * 1.12, 0, 7));
  P.stroke({ c: 'trim', h: 0.66, r: 0.24, m: 0.92 }, R * 0.10, ctx => ctx.arc(cx, cy, R, 0, 7));
  P.stroke({ c: 'trimHi', h: 0.74, r: 0.16, m: 1 }, R * 0.035, ctx => ctx.arc(cx, cy, R * 0.86, 0, 7));
  const spokes = factionIdx === 0 ? 4 : 6;
  for (let i = 0; i < spokes; i++) {
    const a = (i / spokes) * Math.PI * 2 + (factionIdx === 0 ? Math.PI / 4 : 0);
    P.fill({ c: 'trim', h: 0.70, r: 0.22, m: 0.95 }, ctx => {
      const r0 = R * 0.18, r1 = R * 0.80, wA = 0.30;
      ctx.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
      ctx.lineTo(cx + Math.cos(a - wA) * r0, cy + Math.sin(a - wA) * r0);
      ctx.lineTo(cx + Math.cos(a + Math.PI) * r0 * 0.3, cy + Math.sin(a + Math.PI) * r0 * 0.3);
      ctx.lineTo(cx + Math.cos(a + wA) * r0, cy + Math.sin(a + wA) * r0);
      ctx.closePath();
    });
  }
  P.fill({ c: 'trimHi', h: 0.86, r: 0.14, m: 1 }, ctx => ctx.arc(cx, cy, R * 0.19, 0, 7));
  P.fill({ c: 'capeLo', k: 0.5, h: 0.6, r: 0.5, m: 0.4 }, ctx => ctx.arc(cx, cy, R * 0.09, 0, 7));
}

/* ---- the face: a cylindrical head unwrap, u=0.5 dead centre front ---- */
function tileFace(P, T, rng) {
  const { x, y, w, h } = T;
  const X = (u) => x + u * w, Y = (v) => y + v * h;   // v: 0 crown .. 1 chin
  const S = (k) => k * w;

  P.rect(x, y, w, h, { c: 'skin', h: 0.5, r: 0.76, m: 0 });
  // overall form shading: temples and jaw fall away from the light
  P.lgrad(X(0.16), 0, X(0.84), 0, [
    [0.00, { c: 'skinLo', k: 0.78, h: 0.36, r: 0.80 }],
    [0.22, { c: 'skin', h: 0.5, r: 0.76 }],
    [0.50, { c: 'skinHi', h: 0.58, r: 0.72 }],
    [0.78, { c: 'skin', h: 0.5, r: 0.76 }],
    [1.00, { c: 'skinLo', k: 0.78, h: 0.36, r: 0.80 }],
  ], ctx => ctx.rect(x, y, w, h));
  // under-jaw shadow
  P.lgrad(0, Y(0.80), 0, Y(1.0), [[0, { c: 'skin', h: 0.5, r: 0.56, a: 0 }], [1, { c: 'skinLo', k: 0.6, h: 0.42, r: 0.6 }]],
    ctx => ctx.rect(x, Y(0.80), w, h * 0.2));

  // hair mass: crown + back + sideburns
  P.fill({ c: 'hair', h: 0.62, r: 0.5, m: 0.02 }, ctx => {
    ctx.moveTo(x, y); ctx.lineTo(x + w, y); ctx.lineTo(x + w, Y(0.30));
    ctx.bezierCurveTo(X(0.86), Y(0.34), X(0.74), Y(0.16), X(0.62), Y(0.20));
    ctx.bezierCurveTo(X(0.56), Y(0.22), X(0.50), Y(0.235), X(0.44), Y(0.20));
    ctx.bezierCurveTo(X(0.30), Y(0.155), X(0.16), Y(0.34), x, Y(0.30));
    ctx.closePath();
  });
  // hair strands
  for (let i = 0; i < 260; i++) {
    const u = rng.next(), v0 = 0.0, v1 = 0.14 + rng.next() * 0.20;
    P.stroke({ c: rng.next() < 0.3 ? 'hairHi' : 'hair', k: rng.next() < 0.3 ? 1 : 0.65, h: 0.66, r: 0.46, a: 0.35 },
      1 + rng.next() * 2, ctx => { ctx.moveTo(X(u), Y(v0)); ctx.lineTo(X(u + (rng.next() - 0.5) * 0.04), Y(v1)); });
  }
  // sideburns / temple hair
  for (const su of [0.20, 0.80]) {
    P.fill({ c: 'hair', h: 0.58, r: 0.5, a: 0.9 }, ctx => {
      ctx.ellipse(X(su), Y(0.36), S(0.075), S(0.15), 0, 0, 7);
    });
  }

  // ears
  for (const eu of [0.155, 0.845]) {
    P.fill({ c: 'skinLo', k: 1.05, h: 0.62, r: 0.58 }, ctx => ctx.ellipse(X(eu), Y(0.50), S(0.030), S(0.055), 0, 0, 7));
    P.fill({ c: 'skinLo', k: 0.85, h: 0.42, r: 0.6 }, ctx => ctx.ellipse(X(eu), Y(0.505), S(0.016), S(0.032), 0, 0, 7));
  }

  // brow ridge (raised) and eye sockets (recessed)
  P.rgrad(X(0.5), Y(0.375), 0, S(0.22), [[0, { c: 'skinHi', k: 1.02, h: 0.70, r: 0.54 }], [1, { c: 'skin', h: 0.5, r: 0.56, a: 0 }]],
    ctx => ctx.ellipse(X(0.5), Y(0.375), S(0.22), S(0.05), 0, 0, 7), 0.9);
  for (const s of [-1, 1]) {
    const eu = 0.5 + s * 0.082;
    P.rgrad(X(eu), Y(0.455), 0, S(0.085), [[0, { c: 'skinLo', k: 0.68, h: 0.36, r: 0.78 }], [1, { c: 'skin', h: 0.5, r: 0.74, a: 0 }]],
      ctx => ctx.ellipse(X(eu), Y(0.455), S(0.085), S(0.052), 0, 0, 7), 0.82);
  }

  // eyes
  for (const s of [-1, 1]) {
    const eu = 0.5 + s * 0.080, ev = 0.462;
    // sclera
    P.fill({ c: '#b8aa94', h: 0.44, r: 0.62, m: 0 }, ctx => {
      ctx.moveTo(X(eu - 0.048 * 1), Y(ev + 0.002));
      ctx.quadraticCurveTo(X(eu), Y(ev - 0.030), X(eu + 0.048), Y(ev + 0.004));
      ctx.quadraticCurveTo(X(eu), Y(ev + 0.026), X(eu - 0.048), Y(ev + 0.002));
      ctx.closePath();
    });
    // iris + pupil
    P.fill({ c: '#3a2a1b', h: 0.42, r: 0.22, m: 0 }, ctx => ctx.ellipse(X(eu + s * 0.004), Y(ev + 0.001), S(0.020), S(0.020), 0, 0, 7));
    P.fill({ c: '#0a0806', h: 0.40, r: 0.20, m: 0 }, ctx => ctx.ellipse(X(eu + s * 0.004), Y(ev + 0.001), S(0.010), S(0.010), 0, 0, 7));
    P.fill({ c: '#ffffff', h: 0.44, r: 0.10, m: 0, a: 0.9 }, ctx => ctx.ellipse(X(eu + s * 0.004 - 0.007), Y(ev - 0.007), S(0.005), S(0.005), 0, 0, 7));
    // upper lash line — heavy, angled, gives the fierce DW look
    P.stroke({ c: '#2a1c12', h: 0.52, r: 0.66, m: 0 }, S(0.0135), ctx => {
      ctx.moveTo(X(eu - 0.050), Y(ev + 0.001));
      ctx.quadraticCurveTo(X(eu), Y(ev - 0.032), X(eu + 0.050), Y(ev + 0.003));
    });
    // eyebrow
    P.fill({ c: 'hair', h: 0.66, r: 0.5 }, ctx => {
      const b0 = eu - s * 0.062, b1 = eu + s * 0.055;
      ctx.moveTo(X(b0), Y(0.404));
      ctx.quadraticCurveTo(X((b0 + b1) / 2), Y(0.376), X(b1), Y(0.398));
      ctx.lineTo(X(b1), Y(0.417));
      ctx.quadraticCurveTo(X((b0 + b1) / 2), Y(0.400), X(b0), Y(0.432));
      ctx.closePath();
    });
  }

  // nose: raised bridge + shadow + nostrils
  P.lgrad(X(0.47), 0, X(0.53), 0, [
    [0, { c: 'skinLo', k: 0.66, h: 0.52, r: 0.76 }],
    [0.45, { c: 'skinHi', k: 0.90, h: 0.74, r: 0.70 }],
    [1, { c: 'skinLo', k: 0.62, h: 0.50, r: 0.76 }],
  ], ctx => {
    ctx.moveTo(X(0.484), Y(0.40)); ctx.lineTo(X(0.516), Y(0.40));
    ctx.lineTo(X(0.528), Y(0.585)); ctx.lineTo(X(0.472), Y(0.585)); ctx.closePath();
  }, 0.95);
  P.rgrad(X(0.5), Y(0.578), 0, S(0.05), [[0, { c: 'skinHi', k: 0.90, h: 0.78, r: 0.70 }], [1, { c: 'skin', h: 0.56, r: 0.74, a: 0 }]],
    ctx => ctx.ellipse(X(0.5), Y(0.578), S(0.042), S(0.028), 0, 0, 7), 0.95);
  for (const s of [-1, 1]) {
    P.fill({ c: 'skinLo', k: 0.55, h: 0.40, r: 0.6, a: 0.85 }, ctx => ctx.ellipse(X(0.5 + s * 0.026), Y(0.590), S(0.011), S(0.007), 0, 0, 7));
  }
  P.lgrad(0, Y(0.585), 0, Y(0.625), [[0, { c: 'skinLo', k: 0.72, h: 0.44, r: 0.58 }], [1, { c: 'skin', h: 0.5, r: 0.56, a: 0 }]],
    ctx => ctx.ellipse(X(0.5), Y(0.60), S(0.055), S(0.03), 0, 0, 7), 0.7);
  // A single warm shadow-side bridge contour survives the atlas mip chain and
  // keeps the modelled nose legible when the face is only tens of pixels tall.
  P.stroke({ c: 'skinLo', k: 0.54, h: 0.44, r: 0.78, a: 0.76 }, S(0.0065), ctx => {
    ctx.moveTo(X(0.480), Y(0.455));
    ctx.bezierCurveTo(X(0.476), Y(0.505), X(0.474), Y(0.555), X(0.468), Y(0.586));
  });

  // mouth
  P.fill({ c: '#965747', h: 0.56, r: 0.64, a: 0.82 }, ctx => {
    ctx.moveTo(X(0.455), Y(0.686));
    ctx.quadraticCurveTo(X(0.5), Y(0.668), X(0.545), Y(0.686));
    ctx.quadraticCurveTo(X(0.5), Y(0.700), X(0.455), Y(0.686)); ctx.closePath();
  });
  P.stroke({ c: '#491b14', h: 0.44, r: 0.62, a: 0.98 }, S(0.0105), ctx => {
    ctx.moveTo(X(0.452), Y(0.688)); ctx.quadraticCurveTo(X(0.5), Y(0.697), X(0.548), Y(0.688));
  });
  P.fill({ c: '#a66553', h: 0.58, r: 0.62, a: 0.76 }, ctx => {
    ctx.moveTo(X(0.462), Y(0.694));
    ctx.quadraticCurveTo(X(0.5), Y(0.716), X(0.538), Y(0.694));
    ctx.quadraticCurveTo(X(0.5), Y(0.700), X(0.462), Y(0.694)); ctx.closePath();
  });
  // chin + cheekbones
  P.rgrad(X(0.5), Y(0.80), 0, S(0.09), [[0, { c: 'skinHi', k: 1.02, h: 0.64, r: 0.54 }], [1, { c: 'skin', h: 0.5, r: 0.56, a: 0 }]],
    ctx => ctx.ellipse(X(0.5), Y(0.80), S(0.09), S(0.06), 0, 0, 7), 0.55);
  for (const s of [-1, 1]) {
    P.rgrad(X(0.5 + s * 0.135), Y(0.56), 0, S(0.10), [[0, { c: 'skinHi', k: 1.02, h: 0.62, r: 0.54 }], [1, { c: 'skin', h: 0.5, r: 0.56, a: 0 }]],
      ctx => ctx.ellipse(X(0.5 + s * 0.135), Y(0.56), S(0.10), S(0.075), 0, 0, 7), 0.45);
  }
  // stubble
  for (let i = 0; i < 120; i++) {
    const u = 0.5 + (rng.next() - 0.5) * 0.44, v = 0.70 + rng.next() * 0.20;
    if (Math.abs(u - 0.5) > 0.19 * (1.4 - v)) continue;
    P.fill({ c: 'hair', h: 0.5, r: 0.58, a: 0.035 }, ctx => ctx.arc(X(u), Y(v), 1 + rng.next(), 0, 7));
  }
  // back-of-head skin should never show: paint it as hair at the seam
  P.rect(x, y, w * 0.115, h, { c: 'hair', h: 0.5, r: 0.5, a: 0.92 });
  P.rect(x + w * 0.885, y, w * 0.115, h, { c: 'hair', h: 0.5, r: 0.5, a: 0.92 });
  P.lgrad(x + w * 0.10, 0, x + w * 0.22, 0, [[0, { c: 'hair', h: 0.5, r: 0.52 }], [1, { c: 'skin', h: 0.5, r: 0.56, a: 0 }]],
    ctx => ctx.rect(x + w * 0.10, y, w * 0.12, h), 0.75);
  P.lgrad(x + w * 0.90, 0, x + w * 0.78, 0, [[0, { c: 'hair', h: 0.5, r: 0.52 }], [1, { c: 'skin', h: 0.5, r: 0.56, a: 0 }]],
    ctx => ctx.rect(x + w * 0.78, y, w * 0.12, h), 0.75);
}

/** rounded rect path helper */
function rr(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/* ------------------------------------------------------------------ normals */

function normalFromHeight(heightCanvas, strength = 2.6) {
  const w = heightCanvas.width, h = heightCanvas.height;
  const src = heightCanvas.getContext('2d').getImageData(0, 0, w, h).data;
  const out = new Uint8ClampedArray(w * h * 4);
  const H = (x, y) => {
    x = x < 0 ? 0 : x >= w ? w - 1 : x;
    y = y < 0 ? 0 : y >= h ? h - 1 : y;
    return src[(y * w + x) * 4] / 255;
  };
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (H(x + 1, y) - H(x - 1, y)) * strength;
      const dy = (H(x, y + 1) - H(x, y - 1)) * strength;
      let nx = -dx, ny = dy, nz = 1;
      const l = Math.hypot(nx, ny, nz);
      nx /= l; ny /= l; nz /= l;
      const i = (y * w + x) * 4;
      out[i] = (nx * 0.5 + 0.5) * 255;
      out[i + 1] = (ny * 0.5 + 0.5) * 255;
      out[i + 2] = (nz * 0.5 + 0.5) * 255;
      out[i + 3] = 255;
    }
  }
  const cv = makeCanvas(w, h);
  cv.getContext('2d').putImageData(new ImageData(out, w, h), 0, 0);
  return cv;
}


/* -------------------------------------------------------------- environment */

/**
 * A tiny procedural sky/ground probe, pre-filtered through PMREM.
 *
 * Without this, `metalness ~ 1` surfaces have nothing to reflect and render
 * near-black: a steel blade only lights up on the exact specular lobe of the
 * sun. This is the single biggest difference between "3D primitives" and
 * "armour" — a warm sun-side, a cool sky-side and a dark ground bounce give
 * every curved plate a travelling highlight as the body turns.
 */
export function buildEnvironment(renderer, sunDir) {
  const w = 256, h = 128;
  const cv = makeCanvas(w, h);
  const c = cv.getContext('2d');
  // sky -> horizon -> ground, equirectangular (y = 0 is up)
  const g = c.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0.00, '#3f68ba');
  g.addColorStop(0.34, '#8fb2df');
  g.addColorStop(0.48, '#dfe4e2');
  g.addColorStop(0.52, '#c9b48c');
  g.addColorStop(0.72, '#6d5c40');
  g.addColorStop(1.00, '#2b2419');
  c.fillStyle = g; c.fillRect(0, 0, w, h);
  // sun blob + warm bounce around it
  const sd = sunDir || { x: 0.4, y: 0.65, z: 0.3 };
  const len = Math.hypot(sd.x, sd.y, sd.z) || 1;
  const sx = sd.x / len, sy = sd.y / len, sz = sd.z / len;
  const su = ((Math.atan2(sx, -sz) / (Math.PI * 2)) + 0.5) * w;
  const sv = (Math.acos(Math.max(-1, Math.min(1, sy))) / Math.PI) * h;
  let rg = c.createRadialGradient(su, sv, 0, su, sv, w * 0.28);
  rg.addColorStop(0, 'rgba(255,238,205,0.95)');
  rg.addColorStop(0.25, 'rgba(255,214,150,0.45)');
  rg.addColorStop(1, 'rgba(255,200,140,0)');
  c.fillStyle = rg; c.fillRect(0, 0, w, h);
  rg = c.createRadialGradient(su, sv, 0, su, sv, w * 0.045);
  rg.addColorStop(0, 'rgba(255,255,246,1)');
  rg.addColorStop(1, 'rgba(255,240,210,0)');
  c.fillStyle = rg; c.fillRect(0, 0, w, h);
  // a few cloud banks so the reflection is not a dead gradient
  for (let i = 0; i < 8; i++) {
    const cx = (i * 97 % w), cy = h * (0.20 + (i % 3) * 0.07);
    const r = w * (0.05 + (i % 4) * 0.02);
    const cg = c.createRadialGradient(cx, cy, 0, cx, cy, r);
    cg.addColorStop(0, 'rgba(245,246,250,0.55)');
    cg.addColorStop(1, 'rgba(245,246,250,0)');
    c.fillStyle = cg; c.fillRect(cx - r, cy - r, r * 2, r * 2);
  }

  /* ---- the sun has to be BRIGHTER THAN WHITE, or metal cannot shine ----
   *
   * Everything above is drawn on a canvas, and a canvas cannot hold a value
   * above 1.0. Fed in as an sRGB CanvasTexture the whole probe therefore
   * ceilings at 1.0 linear, so with envMapIntensity 1.25 the brightest thing a
   * mirror-metal helmet could ever reflect was ~1.25 — against a bloom
   * prefilter threshold of 2.05. Armour specular could not clear bloom as a
   * matter of arithmetic, and the frame measured p99 212 with 0.06% of pixels
   * over 200: the missing specular anchor in the lighting r2 verdict. A grade
   * cannot fix that (see the correction at e6f55de) because there was no
   * highlight to stop clipping — the renderer was never producing one.
   *
   * So the gradient, clouds and ground bounce stay exactly as painted, and the
   * canvas is lifted into a float buffer where the sun disc is re-added at a
   * real radiance. Same art, one channel of headroom.
   */
  const px = c.getImageData(0, 0, w, h).data;
  const data = new Float32Array(w * h * 4);
  const srgbToLin = (v) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  for (let i = 0; i < w * h; i++) {
    data[i * 4] = srgbToLin(px[i * 4] / 255);
    data[i * 4 + 1] = srgbToLin(px[i * 4 + 1] / 255);
    data[i * 4 + 2] = srgbToLin(px[i * 4 + 2] / 255);
    data[i * 4 + 3] = 1;
  }
  // A disc of SUN_RADIANCE with a soft skirt, added on top in linear space.
  // 34 clears the 2.05 bloom prefilter by enough that a curved plate keeps a
  // hot edge as it turns, without the whole probe reading as an overcast sky.
  const SUN_RADIANCE = 34, SUN_R = w * 0.030, SKIRT = w * 0.10;
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      // wrap in u so a sun near the seam is not clipped in half
      let du = i - su; if (du > w * 0.5) du -= w; else if (du < -w * 0.5) du += w;
      const d = Math.hypot(du, j - sv);
      let k = 0;
      if (d <= SUN_R) k = 1;
      else if (d < SKIRT) { const f = 1 - (d - SUN_R) / (SKIRT - SUN_R); k = f * f * 0.16; }
      if (k <= 0) continue;
      const o = (j * w + i) * 4, e = SUN_RADIANCE * k;
      data[o] += e; data[o + 1] += e * 0.955; data[o + 2] += e * 0.86;
    }
  }
  const t = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.FloatType);
  t.mapping = THREE.EquirectangularReflectionMapping;
  t.colorSpace = THREE.LinearSRGBColorSpace;   // the buffer is already linear
  t.needsUpdate = true;
  try {
    const pm = new THREE.PMREMGenerator(renderer);
    pm.compileEquirectangularShader();
    const rt = pm.fromEquirectangular(t);
    pm.dispose(); t.dispose();
    return rt.texture;
  } catch (e) {
    return t;   // fall back to the raw probe rather than losing metals entirely
  }
}

/* ------------------------------------------------------------------ public */

function tex(canvas, { srgb = false, aniso = 4 } = {}) {
  const t = new THREE.CanvasTexture(canvas);
  t.flipY = false;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = aniso;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  return t;
}

/**
 * Build every character texture + material. Call once at boot.
 * @returns {{ materials: Record<string,THREE.Material>, textures: object,
 *             uv: typeof UVRECT, factions: typeof FACTIONS, dispose():void }}
 */
export function buildCharacterMaterials(renderer, seed = 91117) {
  const keys = ['sui', 'kai', 'wei'];
  const albedo = keys.map(k => {
    const cv = makeCanvas(AW, AH);
    const ctx = cv.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    return { cv, ctx, pal: FACTIONS[k], kind: 'a' };
  });
  const hcv = makeCanvas(AW, AH), ocv = makeCanvas(AW, AH);
  const hL = { cv: hcv, ctx: hcv.getContext('2d'), kind: 'h' };
  const oL = { cv: ocv, ctx: ocv.getContext('2d'), kind: 'o' };
  hL.ctx.fillStyle = 'rgb(128,128,128)'; hL.ctx.fillRect(0, 0, AW, AH);
  oL.ctx.fillStyle = 'rgb(255,153,0)'; oL.ctx.fillRect(0, 0, AW, AH);
  const P = new Painter([...albedo, hL, oL]);

  // Same RNG stream for every layer => identical patterns, different palettes.
  const draw = (fn, tileName, idx) => {
    const T = TILES[tileName];
    P.clip(T.x, T.y, T.w, T.h, () => fn(P, T, new RNG(seed + idx * 7919), idx));
  };
  draw(tileFace, 'face', 1);
  draw(tileLamellar, 'lamellar', 2);
  draw(tileCloth, 'cloth', 3);
  draw(tileSteel, 'steel', 4);
  draw(tileGold, 'gold', 5);
  draw(tileSkin, 'skin', 6);
  draw(tileLeather, 'leather', 7);
  draw(tileSilk, 'silk', 8);
  draw(tileHair, 'hair', 9);
  draw(tileWood, 'wood', 10);
  draw(tileIron, 'iron', 11);
  draw(tileNeutral, 'neutral', 12);
  // the cape sigil differs per faction, so paint it per albedo layer
  {
    const T = TILES.cape;
    for (let i = 0; i < albedo.length; i++) {
      // shared height/roughness layers only need painting once
      const sub = new Painter(i === 0 ? [albedo[i], hL, oL] : [albedo[i]]);
      sub.clip(T.x, T.y, T.w, T.h, () => tileCape(sub, T, new RNG(seed + 4242), i));
    }
  }

  const aniso = Math.min(8, renderer?.capabilities?.getMaxAnisotropy?.() ?? 4);
  const envMap = renderer ? buildEnvironment(renderer) : null;
  const normalCv = normalFromHeight(hcv, 2.4);
  const normalMap = tex(normalCv, { aniso });
  const ormMap = tex(ocv, { aniso });

  const textures = { normalMap, ormMap, albedo: {} };
  const materials = {};
  keys.forEach((k, i) => {
    const map = tex(albedo[i].cv, { srgb: true, aniso });
    textures.albedo[k] = map;
    const m = new THREE.MeshStandardMaterial({
      map, normalMap, roughnessMap: ormMap, metalnessMap: ormMap,
      roughness: 1.0, metalness: 1.0,
      vertexColors: true,
      normalScale: new THREE.Vector2(0.85, 0.85),
      envMap, envMapIntensity: 1.25,
      side: THREE.FrontSide,
      shadowSide: THREE.DoubleSide,
    });
    m.name = 'char_' + k;
    materials[k] = m;
  });
  // A depth-only material variant is not needed; three derives it.

  // Flat silhouette material for the far LOD — same atlas, no normal detail,
  // rendered double sided because a cutout has no back.
  keys.forEach((k) => {
    const m = new THREE.MeshStandardMaterial({
      map: textures.albedo[k], roughnessMap: ormMap, metalnessMap: ormMap,
      roughness: 1.0, metalness: 1.0, vertexColors: true,
      side: THREE.DoubleSide, envMap, envMapIntensity: 1.0,
    });
    m.name = 'charFlat_' + k;
    materials[k + 'Flat'] = m;
  });

  textures.envMap = envMap;
  return {
    materials, textures, uv: UVRECT, tiles: TILES, factions: FACTIONS, envMap,
    /** material for an entity team id */
    forTeam(team) { return team === 1 ? materials.kai : team === 2 ? materials.wei : materials.sui; },
    flatForTeam(team) { return team === 1 ? materials.kaiFlat : team === 2 ? materials.weiFlat : materials.suiFlat; },
    dispose() {
      for (const k in materials) materials[k].dispose();
      normalMap.dispose(); ormMap.dispose(); envMap?.dispose?.();
      for (const k in textures.albedo) textures.albedo[k].dispose();
    },
  };
}
